<template>
  <div
    ref="gameEl"
    class="addon gameplay"
    :class="{
      'gameplay--pointer-events-none': campaignState.isEditModeActive,
      'gameplay--bottom-fill': hasIndicatorBottom && hasIndicatorTop
    }"
  >
    <GameIndicators v-if="hasIndicatorTop" :indicators="state.settings?.game?.state.indicators"></GameIndicators>
    <component :is="comp" v-if="isReady && state.settings?.game" key="game" :model="state.settings.game" />
    <GameIndicators v-if="!hasIndicatorTop" :indicators="state.settings?.game?.state.indicators"></GameIndicators>
  </div>
</template>

<script lang="ts">
import { Component, computed, defineAsyncComponent, defineComponent, nextTick, PropType, ref } from 'vue';
import { AddonGameplayModel } from '@/components/addons/gameplay/Model';
import { useStore } from '@/store';
import { isSsr } from '@/utilities/Utilities';
import GameIndicators from '@/components/indicators/View.vue';
import { GameIndicatorTypeState, GameIndicatorVAlignTypes } from '@/components/indicators/Model';

const gameComponent: { [key: string]: () => Promise<Component> } = {
  video_quiz: () => import('@/components/games/video-quiz/View.vue'),
  slotmachine: () => import('@/components/games/slot-machine/View.vue'),
  guessthepicture: () => import('@/components/games/guessthepicture/View.vue'),
  poll: () => import('@/components/games/poll/View.vue'),
  placetheitem: () => import('@/components/games/placetheitem/View.vue'),
  scratchcard: () => import('@/components/games/scratchcard/View.vue'),
  hitthetarget: () => import('@/components/games/hitthetarget/View.vue'),
  prediction: () => import('@/components/games/prediction/View.vue'),
  guesstheword: () => import('@/components/games/guesstheword/View.vue'),
  finderrors: () => import('@/components/games/spotthedifference/View.vue'),
  memory: () => import('@/components/games/memory/View.vue'),
  dice: () => import('@/components/games/dice/View.vue'),
  shootit: () => import('@/components/games/shootit/View.vue'),
  snake: () => import('@/components/games/snake/View.vue'),
  spinthebottle: () => import('@/components/games/spinthebottle/View.vue'),
  swipeit: () => import('@/components/games/swipeit/View.vue'),
  wof: () => import('@/components/games/wof/View.vue'),
  luckynumber: () => import('@/components/games/luckynumber/View.vue'),
  rockpaperscissors: () => import('@/components/games/rockpaperscissors/View.vue'),
  dropgame: () => import('@/components/games/dropgame/View.vue'),
  shell: () => import('@/components/games/shell/View.vue'),
  slidingpuzzle: () => import('@/components/games/sliding-puzzle/View.vue'),
  product_selector: () => import('@/components/games/product-selector/View.vue'),
  campaign_link: () => import('@/components/games/campaign-link/View.vue'),
  personality_test: () => import('@/components/games/personality-test/View.vue'),
  quiz: () => import('@/components/games/quiz/View.vue'),
  calendar: () => import('@/components/games/calendar/View.vue'),
  survey: () => import('@/components/games/survey/View.vue'),
  priority: () => import('@/components/games/priority/View.vue'),
  puzzle: () => import('@/components/games/puzzle/View.vue')
};

export default defineComponent({
  name: 'AddonGameplay',
  components: {
    GameIndicators
  },
  props: {
    model: {
      type: Object as PropType<AddonGameplayModel>,
      required: true
    }
  },
  setup(props) {
    const store = useStore();
    const model = props.model as AddonGameplayModel;
    const state = model.state;
    const gameEl = ref<HTMLElement | null>(null);
    const campaignState = store.getters['campaign/state'];
    const isReady = ref(!state.hasInstantWin && !isSsr());

    let readyPromiseResolve: (() => void) | undefined;

    const hasIndicatorTop = computed(() => {
      return state.settings?.game?.state?.indicators?.state?.indicators.find(
        (el: GameIndicatorTypeState) => el.valign === GameIndicatorVAlignTypes.TOP
      );
    });

    const hasIndicatorBottom = computed(() => {
      return state.settings?.game?.state?.indicators?.state?.indicators.find(
        (el: GameIndicatorTypeState) => el.valign === GameIndicatorVAlignTypes.BOTTOM
      );
    });

    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    const comp = defineAsyncComponent({
      loader: async () => {
        if (!state.settings?.gameAlias) {
          throw new Error('Missing game alias');
        }

        const component = await gameComponent[state.settings.gameAlias]();

        await nextTick();

        if (readyPromiseResolve) {
          readyPromiseResolve();
        }

        return component;
      }
    });

    if (isSsr() && readyPromiseResolve) {
      readyPromiseResolve();
    }

    if (state.hasInstantWin && !isSsr()) {
      model
        .fetchInstantWinData()
        .then(() => {
          const startTime = new Date().getTime();
          setTimeout(() => {
            isReady.value = true;
          }, Math.max(0, 625 - (new Date().getTime() - startTime)));
        })
        .catch(() => {
          isReady.value = true;
        });
    }

    return {
      comp,
      state,
      campaignState,
      gameEl,
      isReady,
      hasIndicatorTop,
      hasIndicatorBottom,
      onBeforeEnter: async () => {
        await readyPromise;

        // Not sure why 2 nextTicks are needed. But they are...
        await nextTick();
        await nextTick();

        return gameEl.value;
      }
    };
  }
});
</script>

<style lang="scss">
.gameplay {
  &--pointer-events-none {
    pointer-events: none;
    * {
      pointer-events: none !important;
    }
  }
  .content__item--action {
    margin-bottom: 0;
  }
}
</style>
